import { useEffect, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Form, Input, Select, Typography } from 'antd'
import type { ContractDocumentStatus, ContractTemplateType } from '@super-mega/server/contracts/contract'
import { EMPTY_CONTENT } from '@/features/contract/const/empty-content.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { ContractLexicalEditor, type ContractLexicalContent } from './contract-lexical-editor.tsx'

type TemplateType = ContractTemplateType

export type ContractDocumentRecord = {
  id: number
  contractNo: string
  title: string
  type: TemplateType
  status: ContractDocumentStatus
  templateId: number | null
  templateVersionId: number | null
  rentalOrderId: number | null
  content?: unknown
  note: string | null
}

type FormValues = {
  title: string
  templateId?: number
  templateVersionId?: number
  note?: string
}

type ContractDocumentEditorModalProps = {
  readOnly?: boolean
  editing?: ContractDocumentRecord | 'new'
  onClose: () => void
  onSaved: () => Promise<void>
}

export function ContractDocumentEditorModal(props: ContractDocumentEditorModalProps) {
  const editingKey = props.editing === 'new' ? 'new' : props.editing ? String(props.editing.id) : 'closed'
  return <ContractDocumentEditorModalContent key={editingKey} {...props} />
}

function ContractDocumentEditorModalContent({
  editing,
  onClose,
  onSaved,
  readOnly = false
}: ContractDocumentEditorModalProps) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const [draftContent, setDraftContent] = useState<ContractLexicalContent | null>(null)
  const templateId = Form.useWatch('templateId', form)
  const templates = useQuery({
    ...trpc.lookup.templates.queryOptions({ purpose: 'contract.document.read' }),
    enabled: editing === 'new'
  })
  const versions = useQuery({
    ...trpc.contract.template.versions.queryOptions({ templateId: templateId ?? 0 }),
    enabled: editing === 'new' && Boolean(templateId)
  })
  const detail = useQuery({
    ...trpc.contract.document.detail.queryOptions({ id: typeof editing === 'object' ? editing.id : 0 }),
    enabled: typeof editing === 'object'
  })
  const create = useMutation(trpc.contract.document.create.mutationOptions())
  const update = useMutation(trpc.contract.document.updateDraft.mutationOptions())
  const publishedVersions = versions.data?.filter((item) => item.status === 'published') ?? []
  const contentReady = editing === 'new' || detail.isSuccess
  const editingKey = editing === 'new' ? 'new' : editing ? String(editing.id) : 'closed'
  const content = draftContent ?? (detail.data?.content as ContractLexicalContent | undefined) ?? EMPTY_CONTENT
  const editorKey = `${editingKey}-${contentReady ? 'ready' : 'loading'}`
  const initialValues: Partial<FormValues> =
    editing === 'new'
      ? { note: '', title: '', templateId: undefined, templateVersionId: undefined }
      : editing
        ? { note: editing.note ?? undefined, title: editing.title }
        : {}
  useEffect(() => {
    if (editing !== 'new' || !templateId || !versions.isFetched) {
      return
    }
    const availablePublishedVersions = versions.data?.filter((item) => item.status === 'published') ?? []
    const currentVersion = form.getFieldValue('templateVersionId')
    if (!availablePublishedVersions.some((item) => item.id === currentVersion)) {
      form.setFieldValue('templateVersionId', availablePublishedVersions[0]?.id)
    }
  }, [editing, form, templateId, versions.data, versions.isFetched])

  const save = async (values: FormValues) => {
    try {
      if (editing === 'new') {
        if (!values.templateVersionId) {
          void message.error('请选择已发布的合同模板版本')
          return false
        }
        await create.mutateAsync({
          note: values.note?.trim() || null,
          rentalOrderId: null,
          templateVersionId: values.templateVersionId,
          title: values.title.trim()
        })
      } else if (editing) {
        await update.mutateAsync({
          id: editing.id,
          data: {
            content,
            note: values.note?.trim() || null,
            printSettings: detail.data?.printSettings as Record<string, unknown> | null,
            title: values.title.trim()
          }
        })
      }
      void message.success(editing === 'new' ? '合同文件已创建' : '合同文件已保存')
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '合同文件保存失败'))
      return false
    }
  }

  const isReadOnly = readOnly || (typeof editing === 'object' && editing.status !== 'confirmed')

  return (
    <ModalForm<FormValues>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={
        isReadOnly
          ? false
          : {
              resetButtonProps: { onClick: onClose },
              searchConfig: { resetText: '取消', submitText: editing === 'new' ? '创建合同' : '保存合同' },
              submitButtonProps: {
                disabled:
                  !contentReady ||
                  detail.isError ||
                  (editing === 'new' && (versions.isFetching || publishedVersions.length === 0)),
                loading: create.isPending || update.isPending
              }
            }
      }
      title={editing === 'new' ? '从模板生成合同文件' : `合同文件 · ${editing?.contractNo ?? ''}`}
      width="68rem"
    >
      <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        <Form.Item label="合同名称" name="title" rules={[{ required: true, message: '请输入合同名称' }]}>
          <Input disabled={isReadOnly} maxLength={255} placeholder="如：贾老板企业设立租赁合同" />
        </Form.Item>
        {editing === 'new' ? (
          <Form.Item label="合同模板" name="templateId" rules={[{ required: true, message: '请选择合同模板' }]}>
            <Select
              loading={templates.isLoading}
              onChange={() => form.setFieldValue('templateVersionId', undefined)}
              options={templates.data ?? []}
              placeholder="请选择模板"
              showSearch={{ optionFilterProp: 'label' }}
            />
          </Form.Item>
        ) : null}
        {editing === 'new' ? (
          <Form.Item
            label="已发布版本"
            name="templateVersionId"
            rules={[{ required: true, message: '请选择模板版本' }]}
          >
            <Select
              disabled={!templateId}
              loading={versions.isLoading}
              options={publishedVersions.map((item) => ({ label: `v${item.version}`, value: item.id }))}
              placeholder="请选择已发布版本"
            />
          </Form.Item>
        ) : null}
      </div>
      <Form.Item label="备注" name="note">
        <Input.TextArea
          disabled={isReadOnly}
          maxLength={5000}
          placeholder="合同生成说明或人工处理备注"
          rows={2}
          showCount
        />
      </Form.Item>
      <div className="mb-2 flex items-center justify-between">
        <Typography.Text strong>合同正文</Typography.Text>
        <Typography.Text type="secondary">
          {editing === 'new'
            ? '正文会从已发布模板复制，创建后可继续编辑'
            : !contentReady
              ? '正在加载合同正文…'
              : isReadOnly
                ? '当前状态已锁定'
                : '可继续编辑正文并插入动态字段'}
        </Typography.Text>
      </div>
      {editing === 'new' ? (
        <Typography.Paragraph type="secondary">
          创建合同后，系统会把所选模板版本复制为已确认合同正文；如需调整正文，请在创建完成后打开合同编辑。
        </Typography.Paragraph>
      ) : contentReady ? (
        <div className="max-h-[42rem] overflow-y-auto pr-1">
          <ContractLexicalEditor
            key={editorKey}
            initialValue={content}
            onChange={isReadOnly ? undefined : setDraftContent}
            readOnly={isReadOnly}
          />
        </div>
      ) : null}
    </ModalForm>
  )
}
